import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { checkForErrors } from './check-for-errors';
import {
  getPlatformDisplayName,
  isSupportedPlatform,
  sortPlatformsByLabel,
} from './platform-catalog';

/**
 * One supported live-agent platform and the JSON schema describing the
 * per-platform config fields (rendered dynamically in the Add/Edit modal).
 *
 * AUTO-4604 split the backend catalog into External (middleware / contact
 * center) and Internal (prime-app native) agent schemas. Both are merged and
 * normalized into this stable shape so the modal + `normalize-config-schema`
 * don't care which source a platform came from. `properties` is a JSON scalar
 * whose runtime shape is normalized at render time.
 */
export interface AgentRoutingSchema {
  /**
   * Option identifier used by the platform picker. For internal schemas this is
   * the real `agent_routing` enum value. For external integrations it's the
   * integration name (e.g. `ContactCenter`) — NOT a valid `agent_routing`; those
   * connectors persist as `agent_routing: 'external_agent'` with the integration
   * name carried in the config (see the modal's submit).
   */
  agent_routing: string;
  /** Human label shown in the platform picker. */
  label: string;
  description?: string | null;
  /** Config-field schema (JSON); shape normalized in `normalizeConfigSchema`. */
  properties?: unknown;
  /** External (middleware / contact center) integration vs internal native. */
  isExternal: boolean;
  /** External only: callback URL sent as `agent_routing_url` (backend-required). */
  callbackUrl?: string | null;
  /** External only: callback token paired with the callback URL. */
  callbackToken?: string | null;
}

/** Dedupe by `agent_routing`, keeping the first occurrence (external wins). */
const dedupeByRouting = (
  schemas: AgentRoutingSchema[]
): AgentRoutingSchema[] => {
  const seen = new Set<string>();
  return schemas.filter((schema) => {
    if (!schema.agent_routing || seen.has(schema.agent_routing)) {
      return false;
    }
    seen.add(schema.agent_routing);
    return true;
  });
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * External `integrationFields` is a flat `{ fieldKey: "type" }` map (e.g.
 * `{ zendeskApiToken: "string" }`). Convert it to the JSON-Schema shape
 * `normalizeConfigSchema` understands, marking EVERY field required (every
 * external integration field is mandatory). Object-valued entries (already a
 * spec) are passed through.
 */
const externalFieldsToSchema = (integrationFields: unknown): unknown => {
  if (!isObject(integrationFields)) {
    return undefined;
  }

  const entries = Object.entries(integrationFields);

  return {
    properties: Object.fromEntries(
      entries.map(([key, value]) => [
        key,
        isObject(value)
          ? value
          : { type: typeof value === 'string' ? value : 'string' },
      ])
    ),
    required: entries.map(([key]) => key),
  };
};

type ExternalIntegration = {
  integrationName?: string | null;
  integrationFields?: unknown;
  callbackURL?: string | null;
  callbackToken?: string | null;
} | null;

type InternalSchema = {
  agent_routing?: string | null;
  label?: string | null;
  description?: string | null;
  properties?: unknown;
} | null;

/**
 * Trim the catalog KEY, not just the label.
 *
 * Integration names are typed by hand on the platform side, and every predicate
 * in `platform-catalog` reads a trimmed value — so `" LAAPI_Quiq "` is offered
 * and displays correctly. Without trimming here the RAW invariant then carries
 * the spaces into `platformId`, and from there into `integration_name` /
 * `integration_headers.integrationName`, which the legacy Console reads: the UI
 * looks right while what persists cannot be matched. A stored connection that
 * already carries the spaces simply stops resolving a schema and takes the
 * pinned-option path, which is the branch built for exactly that absence.
 */
const trimKey = (value?: string | null): string => (value ?? '').trim();

/** External integrations -> stable AgentRoutingSchema shape. */
const fromExternal = (
  integrations: ReadonlyArray<ExternalIntegration> | null | undefined
): AgentRoutingSchema[] =>
  (integrations ?? [])
    .filter((integration) => !!trimKey(integration?.integrationName))
    .map((integration) => ({
      agent_routing: trimKey(integration?.integrationName),
      label: integration?.integrationName ?? '',
      description: null,
      properties: externalFieldsToSchema(integration?.integrationFields),
      isExternal: true,
      // The backend requires `agent_routing_url` for external_agent; it's the
      // integration's callback URL.
      callbackUrl: integration?.callbackURL ?? null,
      callbackToken: integration?.callbackToken ?? null,
    }));

/** Internal schemas already match the stable shape. */
const fromInternal = (
  data: ReadonlyArray<InternalSchema> | null | undefined
): AgentRoutingSchema[] =>
  (data ?? [])
    .filter((schema) => !!trimKey(schema?.agent_routing))
    .map((schema) => ({
      agent_routing: trimKey(schema?.agent_routing),
      label: schema?.label ?? schema?.agent_routing ?? '',
      description: schema?.description ?? null,
      properties: schema?.properties,
      isExternal: false,
    }));

/**
 * Merge both backend families into the FULL catalog: every integration the
 * backend returned, named as a user should read it.
 *
 * Pure on purpose — the network call lives in `getAgentRoutingSchemas`, so this
 * (the part carrying the product rules) is unit-testable.
 *
 * PSTUDIO-4072 narrows what the picker OFFERS, and that narrowing lives in
 * `selectSupportedPlatforms`, not here. The distinction is load-bearing: an
 * existing connection bound to an unsupported platform resolves its SCHEMA out
 * of this catalog, which is what keeps its credential fields editable, and what
 * keeps the submit's "the platform is absent, so trust the stored routing"
 * guard meaning what it says. Filtering here made that guard fire for every
 * legacy connection and left its Edit form with no fields at all.
 *
 * Two invariants:
 * - `agent_routing` stays RAW. It is persisted as `PypeConnection.platformId`
 *   and matched against existing connections, so only `label` is rewritten.
 * - the display name is derived from whatever would have been SHOWN — an
 *   internal schema's backend-provided label (`cc` -> "Contact Center") is
 *   preserved rather than re-derived from its routing value, which would print
 *   "Cc".
 *
 * The result is ordered A-Z by that display name: the backend lists integrations
 * in creation order, which is meaningless to a user scanning the picker.
 */
export const buildPlatformCatalog = (
  externalIntegrations: ReadonlyArray<ExternalIntegration> | null | undefined,
  internalSchemas: ReadonlyArray<InternalSchema> | null | undefined
): AgentRoutingSchema[] => {
  const withDisplayNames = dedupeByRouting([
    ...fromExternal(externalIntegrations),
    ...fromInternal(internalSchemas),
  ]).map((schema) => ({
    ...schema,
    label: getPlatformDisplayName(schema.label),
  }));

  return sortPlatformsByLabel(withDisplayNames);
};

/**
 * What the picker OFFERS: Contact Center plus the `LAAPI_*` middleware
 * connectors, and nothing else (PSTUDIO-4072).
 *
 * The rule reads the raw NAME, not `isExternal` — an internal schema carrying a
 * supported name is offered too.
 *
 * Display names are disambiguated here rather than in `buildPlatformCatalog`,
 * because a collision only matters among the rows a user can actually see. Two
 * hand-typed names can strip to the same display name (`LAAPI_Quiq` and
 * `LAAPI_quiq`), and two identical rows are worse than a long one: `ps-select`'s
 * type-to-select always lands on the first, so the second is unreachable from
 * the keyboard, while picking the wrong one rewrites `platformId` and clears the
 * credentials that were typed.
 */
const disambiguateLabels = (
  options: readonly AgentRoutingSchema[]
): AgentRoutingSchema[] => {
  const labelCounts = new Map<string, number>();
  options.forEach(({ label }) => {
    const key = label.toLowerCase();
    labelCounts.set(key, (labelCounts.get(key) ?? 0) + 1);
  });

  return options.map((schema) =>
    (labelCounts.get(schema.label.toLowerCase()) ?? 0) > 1
      ? { ...schema, label: `${schema.label} (${schema.agent_routing})` }
      : schema
  );
};

export const selectSupportedPlatforms = (
  catalog: readonly AgentRoutingSchema[]
): AgentRoutingSchema[] =>
  sortPlatformsByLabel(
    disambiguateLabels(
      catalog.filter((schema) => isSupportedPlatform(schema.agent_routing))
    )
  );

/**
 * Suffix for a pinned platform Pro Studio no longer offers — the wording the
 * ticket asked for. It exists because the pinned row re-enters the very list
 * the filter cleaned up: legacy `Quiq` next to `LAAPI_Quiq` would otherwise
 * show two rows reading exactly "Quiq".
 */
export const LEGACY_PLATFORM_LABEL_SUFFIX = ' (Legacy)';

/**
 * The offer as the Add/Edit picker should RENDER it: the supported platforms,
 * plus the platform an existing connection is already bound to.
 *
 * Narrowing the offer means a connection created before this change can point at
 * a platform that is no longer offered — opening Edit would then show an empty
 * Platform field, hiding what the connection actually is (`ps-select` derives its
 * display label from the SELECTED OPTION, so with no matching option the field
 * falls back to its placeholder).
 *
 * The pinned row is the catalog's own entry whenever the backend still returns
 * it, schema and callback URL included, so the credential fields of a legacy
 * connection stay editable. Only when the integration is gone from the response
 * entirely is a bare row synthesised — and that absence is then the same signal
 * the submit reads to trust the connection's stored routing.
 */
export const withPinnedPlatformOption = (
  offered: readonly AgentRoutingSchema[],
  catalog: readonly AgentRoutingSchema[],
  platformId?: string | null
): readonly AgentRoutingSchema[] => {
  if (!platformId) {
    return offered;
  }

  if (offered.some((option) => option.agent_routing === platformId)) {
    return offered;
  }

  const fromCatalog = catalog.find(
    (option) => option.agent_routing === platformId
  );
  const displayName = fromCatalog?.label ?? getPlatformDisplayName(platformId);
  // A platform that IS supported but missing from the offer is not legacy — it
  // was removed on the platform side, so saying "Legacy" would state something
  // false about it.
  const label = isSupportedPlatform(platformId)
    ? displayName
    : `${displayName}${LEGACY_PLATFORM_LABEL_SUFFIX}`;

  // Re-sorted, not appended: to the user the pinned entry is an ordinary row of
  // the list, so leaving it at the end would make the picker look unsorted.
  //
  // Disambiguated AFTER the pinned row joins, not only inside the offer: the row
  // that collides may be the pinned one. `LAAPI_quiq` in the offer and a
  // connection on `LAAPI_Quiq` both read "Quiq", and `selectSupportedPlatforms`
  // alone cannot see that — it never sees the pinned entry.
  return sortPlatformsByLabel(
    disambiguateLabels([
      ...offered,
      fromCatalog
        ? { ...fromCatalog, label }
        : {
            agent_routing: platformId,
            label,
            description: null,
            properties: undefined,
            isExternal: false,
          },
    ])
  );
};

export const getAgentRoutingSchemas = async ({
  uniqueCustomerId,
  projectId,
}: {
  uniqueCustomerId: string;
  projectId: string;
}): Promise<{ live: AgentRoutingSchema[]; sandbox: AgentRoutingSchema[] }> => {
  const response = await urqlGql.getAgentRoutingSchemas({
    customerId: uniqueCustomerId,
    projectId,
  });

  checkForErrors(response);

  return {
    live: buildPlatformCatalog(
      response.data?.externalLive?.integrations,
      response.data?.internalLive?.data
    ),
    sandbox: buildPlatformCatalog(
      response.data?.externalSandbox?.integrations,
      response.data?.internalSandbox?.data
    ),
  };
};
